// Smart Sash Admin Panel - Stream Viewer Hook
// Manages LiveKit connection lifecycle, controls, and cleanup for the viewer page

import { ActiveSessionInfo } from '@/models/teaching.models';
import { useAuth } from '@/services/auth';
import { teachingOrchestrationService } from '@/services/grpc/teachingOrchestrationService';
import { useStreamStore, useUIStore } from '@/store';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const GRPC_URL = import.meta.env.VITE_GRPC_URL || 'https://localhost:5001';

const MAX_RECONNECT_ATTEMPTS = 3;
const SESSION_ENDED_REDIRECT_DELAY = 3000;

export type ViewerStatus =
  | 'loading'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'disconnected'
  | 'error';

export const useStreamViewer = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addNotification } = useUIStore();
  const {
    activeSession,
    connectionInfo,
    isJoining,
    joinSession,
    leaveSession,
    setActiveSession,
    clearConnectionInfo,
    sessions,
    fetchSessions,
  } = useStreamStore();

  const [viewerStatus, setViewerStatus] = useState<ViewerStatus>('loading');
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [showInfoOverlay, setShowInfoOverlay] = useState(true);

  const hasJoinedRef = useRef(false);
  const isLeavingRef = useRef(false);
  const videoContainerRef = useRef<HTMLDivElement>(null);

  const institutionId = user?.institutionId ?? '';
  const userId = user?.id ?? '';

  // Find the session from store if activeSession is not set (e.g. direct URL navigation)
  useEffect(() => {
    if (!activeSession && sessionId && sessions.length > 0) {
      const session = sessions.find((s) => s.streamSessionId === sessionId);
      if (session) setActiveSession(session);
    }
  }, [activeSession, sessionId, sessions, setActiveSession]);

  // Initialize gRPC service
  useEffect(() => {
    teachingOrchestrationService.initialize(GRPC_URL);
  }, []);

  // If we navigated directly, load sessions to find our session
  useEffect(() => {
    if (!activeSession && sessionId && sessions.length === 0 && institutionId && userId) {
      fetchSessions({ institutionId, userId, pageNumber: 1, pageSize: 100 });
    }
  }, [activeSession, sessionId, sessions.length, institutionId, userId, fetchSessions]);

  // Join session on mount
  useEffect(() => {
    if (!sessionId || !institutionId || !userId || !activeSession) return;

    let cancelled = false;
    hasJoinedRef.current = false;

    const doJoin = async () => {
      setViewerStatus('connecting');
      try {
        console.log('[StreamViewer] Joining session:', sessionId);
        await joinSession(institutionId, sessionId, activeSession.deviceId, userId);
        if (cancelled) {
          console.log('[StreamViewer] Join completed but effect was cancelled, leaving session');
          leaveSession(institutionId, sessionId, userId);
          return;
        }
        hasJoinedRef.current = true;
        console.log('[StreamViewer] Join successful');
      } catch {
        if (!cancelled) {
          setViewerStatus('error');
        }
      }
    };

    doJoin();

    return () => {
      console.log('[StreamViewer] Join effect cleanup, hasJoined:', hasJoinedRef.current);
      cancelled = true;
      if (hasJoinedRef.current && !isLeavingRef.current) {
        isLeavingRef.current = true;
        leaveSession(institutionId, sessionId, userId);
      }
    };
  }, [sessionId, institutionId, userId, activeSession, joinSession, leaveSession]);

  // Update status when connectionInfo arrives
  useEffect(() => {
    if (connectionInfo?.success) {
      setViewerStatus('connected');
    }
  }, [connectionInfo]);

  // Cleanup: leave session on beforeunload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (sessionId && institutionId && userId && hasJoinedRef.current) {
        leaveSession(institutionId, sessionId, userId);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [sessionId, institutionId, userId, leaveSession]);

  const handleLeave = useCallback(async () => {
    if (isLeavingRef.current) return;
    isLeavingRef.current = true;

    if (sessionId && institutionId && userId) {
      await leaveSession(institutionId, sessionId, userId);
    }
    clearConnectionInfo();
    setActiveSession(null);
    navigate('/streams');
  }, [
    sessionId,
    institutionId,
    userId,
    leaveSession,
    clearConnectionInfo,
    setActiveSession,
    navigate,
  ]);

  const handleSessionEnded = useCallback(() => {
    addNotification({
      type: 'info',
      message: 'Session ended by presenter',
      duration: SESSION_ENDED_REDIRECT_DELAY,
    });
    setTimeout(() => {
      if (!isLeavingRef.current) {
        isLeavingRef.current = true;
        clearConnectionInfo();
        setActiveSession(null);
        navigate('/streams');
      }
    }, SESSION_ENDED_REDIRECT_DELAY);
  }, [addNotification, clearConnectionInfo, setActiveSession, navigate]);

  const handleDisconnected = useCallback(() => {
    if (isLeavingRef.current) return;

    if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
      setViewerStatus('reconnecting');
      setReconnectAttempts((prev) => prev + 1);
    } else {
      setViewerStatus('disconnected');
      handleSessionEnded();
    }
  }, [reconnectAttempts, handleSessionEnded]);

  const handleConnected = useCallback(() => {
    setViewerStatus('connected');
    setReconnectAttempts(0);
  }, []);

  const toggleMute = useCallback(() => setIsMuted((prev) => !prev), []);

  const handleVolumeChange = useCallback((newVolume: number) => {
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    const el = videoContainerRef.current;
    if (!el) return;

    try {
      if (!document.fullscreenElement) {
        await el.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      console.error('Fullscreen toggle failed:', err);
    }
  }, []);

  // Listen for fullscreen changes (e.g. user presses Escape)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleInfoOverlay = useCallback(() => setShowInfoOverlay((prev) => !prev), []);

  return {
    sessionId,
    activeSession: activeSession as ActiveSessionInfo | null,
    connectionInfo,
    isJoining,
    viewerStatus,
    isMuted,
    volume,
    isFullscreen,
    reconnectAttempts,
    showInfoOverlay,
    videoContainerRef,
    handleLeave,
    handleDisconnected,
    handleConnected,
    handleSessionEnded,
    toggleMute,
    handleVolumeChange,
    toggleFullscreen,
    toggleInfoOverlay,
  };
};
